import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	UploadCacheItem,
	UploadCacheItemState,
	UploadBatchHandlers,
	useDocumentUploader
} from "./useDocumentUploader";
import {
	FileThumbnailKind,
	getDisplayFriendlySize,
	getExtensionAndType
} from "client_legacy/Documents/fileExtensions";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import {
	DropOver,
	FormattedText,
	HeightAnimationContainer,
	Icon,
	IconButton,
	Space,
	Spinner,
	UploadCard,
	UploadCardStatusType,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./DocumentUploader.module.less";
import { never } from "client_legacy/ndaw/errors";
import {
	MediaQueryKind,
	useSynchronizedRef,
	useToggle,
	useWorkspaceMediaQuery
} from "client/Hooks";
import classNames from "classnames";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";

export type DocumentUploaderHandle = {
	uploadDocument: (
		files: readonly FileSelectionEntry[],
		handlers: UploadBatchHandlers
	) => void;
	collapse: () => void;
};

export type DocumentUploaderProps = {
	ref: Ref<DocumentUploaderHandle>;
};

export const DocumentUploader = (props: DocumentUploaderProps): ReactElement | null => {
	const {
		ref
	} = props;

	const [entries, setEntries] = useState<readonly UploadCacheItem[]>(EMPTY_ARRAY);

	const [isCollapsed, toggleIsCollapsed] = useToggle(INITIAL_IS_COLLAPSED);
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const entriesRef: ReadonlyRefObject<readonly UploadCacheItem[]> = useSynchronizedRef(entries);

	const {
		addHandler,
		deleteHandler,
		retryHandler,
		nameConflictDialog
	} = useDocumentUploader(
		entries,
		setEntries
	);

	useImperativeHandle(
		ref,
		(): DocumentUploaderHandle => ({
			uploadDocument: (files, handlers) => {
				addHandler(files, handlers);
			},
			collapse: (): void => {
				toggleIsCollapsed(true);
			}
		}),
		[
			addHandler,
			toggleIsCollapsed
		]
	);

	const cancelAllCurrentUploads = useCallback(
		(): void => {
			setEntries(
				(x) => {
					for (const cache of x) {
						const { state } = cache;
						if (state.kind === "uploading") {
							state.cancellation
								.abort();
						}
					}

					return [];
				}
			);
		},
		[]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			const hasPendingItems: boolean = entries
				.some(x => x.state.kind === "pending" || x.state.kind === "uploading");

			if (hasPendingItems) {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToCancelTheUploadsInProgress"),
					children: localize("_YourUploadsAreNotCompleteYetInfo"),
					confirmText: localize("_ContinueUpload"),
					cancelText: localize("_CancelUpload"),
					trackingId: "cancel-document-upload-confirmation-dialog"
				});

				if (result.wasSuccessful) {
					return;
				}
			}

			toggleIsCollapsed(true);
			cancelAllCurrentUploads();
		},
		[
			entries,
			toggleIsCollapsed,
			cancelAllCurrentUploads,
			openConfirmationDialog
		]
	);

	const beforeUnloadListener = useCallback(
		(event: BeforeUnloadEvent): string | void => {
			const hasItemsInProgress: boolean = entriesRef
				.current
				.some(x => x.state.kind === "pending" || x.state.kind === "uploading");

			if (hasItemsInProgress) {
				event.preventDefault();
				event.returnValue = localize("_YourUploadsInProgressWillBeCancelled");
				return event.returnValue;
			}
		},
		[entriesRef]
	);

	useEffect(
		(): (() => void) => {
			const cancellation = new AbortController();

			window.addEventListener(
				"beforeunload",
				beforeUnloadListener,
				{ signal: cancellation.signal }
			);

			return (): void => {
				cancellation.abort();
			};
		},
		[beforeUnloadListener]
	);

	const renderContent = (): ReactElement => {
		const isVisuallyCollapsed: boolean = isCollapsed && !isResponsive;

		return (
			<div
				className={classNames(
					styles.container,
					{
						[styles.container_isInsideDropOver]: isResponsive,
						[styles.container_collapsed]: isVisuallyCollapsed
					}
				)}
			>
				<div className={styles.header}>
					<div
						className={styles.titleContainer}
						onClick={isResponsive
							? undefined
							: () => toggleIsCollapsed()
						}
					>
						<Title entries={entries} />
					</div>
					<div className={styles.buttons}>
						{!isResponsive && (
							<IconButton
								icon={isVisuallyCollapsed
									? "chevron-up"
									: "chevron-down"
								}
								kind="transparent"
								trackingId="toggle"
								shape="round"
								onClick={() => toggleIsCollapsed()}
							/>
						)}
						<IconButton
							icon="xmark"
							kind="secondary"
							trackingId="close"
							shape="round"
							onClick={closeHandler}
						/>
					</div>
				</div>
				<div className={styles.content}>
					<HeightAnimationContainer
						isVisible={!isVisuallyCollapsed}
					>
						<ul className={styles.list}>
							{entries.map((x): ReactElement => (
								<ListItem
									key={x.id}
									item={x}
									onDelete={() => deleteHandler(x.id)}
									onRetry={() => retryHandler(x.id)}
									trackingId={`item-${x.id}`}
								/>
							))}
						</ul>
					</HeightAnimationContainer>
				</div>
			</div>
		);
	};

	if (entries.length <= 0) {
		return null;
	}

	return (
		<>
			{isResponsive
				? (
					<DropOver
						target={document.body}

					>
						{renderContent()}
					</DropOver>
				)
				: (
					<div className={styles.root}>
						{renderContent()}
					</div>
				)
			}
			{nameConflictDialog}
			{confirmationDialog}
		</>
	);
};
DocumentUploader.displayName = "DocumentUploader";

const INITIAL_IS_COLLAPSED: boolean = true;

type TitleProps = {
	entries: readonly UploadCacheItem[];
};

const Title = (props: TitleProps): ReactElement => {
	const {
		entries
	} = props;

	const {
		uploadingCount,
		failedCount,
		succeedCount
	} = useMemo(
		(): {
			uploadingCount: number;
			failedCount: number;
			succeedCount: number;
		} => {
			let uploadingCount: number = 0;
			let failedCount: number = 0;
			let succeedCount: number = 0;

			for (const entry of entries) {
				const { state } = entry;

				switch(state.kind) {
					case "pending":
					case "uploading":
						uploadingCount += 1;
						break;
					case "failed":
						failedCount += 1;
						break;
					case "success":
						succeedCount += 1;
						break;
					default:
						never(state);
				}
			}

			return {
				uploadingCount,
				failedCount,
				succeedCount
			};
		},
		[entries]
	);

	const isAllSucceed: boolean = succeedCount > 0
		&& succeedCount === entries.length;

	const hasFailed: boolean = failedCount > 0;

	return (
		<>
			<h3 className={styles.title}>
				{uploadingCount > 0
					? (
						<FormattedText
							text={localize("_UploadingXFiles")}
							thingsToReplace={[
								{
									what: "{{Amount}}",
									filler: uploadingCount
								}
							]}
						/>
					)
					: succeedCount > 0
						? (
							<FormattedText
								text={localize("_XUploadsCompleted")}
								thingsToReplace={[
									{
										what: "{{Amount}}",
										filler: succeedCount
									}
								]}
							/>
						)
						: null
				}

				{hasFailed && (
					<>
						<Space />
						(
						<FormattedText
							text={localize("_XFailed")}
							thingsToReplace={[
								{
									what: "{{Amount}}",
									filler: failedCount
								}
							]}
						/>
						)
					</>
				)}
			</h3>
			{isAllSucceed && <Icon value={{ kind: "font", name: "circle-check", color: "success" }} />}
			{hasFailed && <Icon value={{ kind: "font", name: "circle-xmark", color: "danger" }} />}
		</>
	);
};
Title.displayName = "title";

type ListItemProps = {
	item: UploadCacheItem;
	onDelete: () => void;
	onRetry: () => void;
	trackingId: string;
};

const ListItem = (props: ListItemProps): ReactElement => {
	const {
		item,
		onDelete,
		onRetry,
		trackingId
	} = props;

	const {
		entry: {
			path: originalPath,
			name,
			size
		},
		state
	} = item;

	const type = useMemo(
		(): FileThumbnailKind => {
			const { type } = getExtensionAndType(name, false);
			return type;
		},
		[name]
	);

	const path: string = state.kind === "success"
		? state.path ?? ""
		: originalPath;

	const status: UploadCardStatusType | undefined = getStatusType(state);

	const openFileHandler = useMemo(
		(): (() => void) | undefined => {
			const { state } = item;

			if (state.kind !== "success") {
				return;
			}

			return state.uploadedFileActions
				?.openFile;
		},
		[item]
	);

	const renderStatus = (): ReactElement => {
		const displayFriendlySize: string = getDisplayFriendlySize(size);

		switch(state.kind) {
			case "uploading":
				return isNotNil(state.progress)
					? (
						<>
							<UploadCard.Status>
								{getDisplayFriendlySize((size * state.progress) / 100)}
								<Space />
								{localize("_Of")}
								<Space />
								{displayFriendlySize}
							</UploadCard.Status>
							<UploadCard.ProgressBar
								progress={state.progress}
							/>
						</>
					) : (
						<>
							<UploadCard.Status>
								{displayFriendlySize}
							</UploadCard.Status>
							<Spinner size="small" />
						</>
					);
			case "failed":
				return (
					<UploadCard.Status>
						{state.message ?? localize("_UploadingNotPossible")}
					</UploadCard.Status>
				);
			case "success":
				return (
					<UploadCard.Status>
						{localize("_Completed")}
					</UploadCard.Status>
				);
			case "pending":
				return (
					<UploadCard.Status>
						{displayFriendlySize}
					</UploadCard.Status>
				);
			default:
				never(state);
		}
	};

	return (
		<li className={styles.listItem} >
			<UploadCard
				trackingId={trackingId}
				status={status}
				onClick={openFileHandler}
			>
				<UploadCard.Thumbnail>
					<Icon
						value={{
							kind: "svg",
							name: type
						}}
						size="extra-extra-large"
					/>
				</UploadCard.Thumbnail>
				<UploadCard.Content>
					<UploadCard.Title>
						{path}
					</UploadCard.Title>

					{renderStatus()}
				</UploadCard.Content>
				<UploadCard.Actions>
					{state.kind === "failed" && (
						<IconButton
							kind="transparent"
							trackingId="retry"
							icon="arrow-rotate-right"
							tooltip={localize("_Retry")}
							onClick={() => onRetry()}
							shape="round"
							bubbleClick={false}
						/>
					)}

					<IconButton
						kind="transparent"
						trackingId="delete"
						icon="xmark"
						tooltip={localize("_Delete")}
						onClick={() => onDelete()}
						shape="round"
						bubbleClick={false}
					/>
				</UploadCard.Actions>
			</UploadCard>
		</li>
	);
};
ListItem.displayName = "ListItem";

function getStatusType(
	state: UploadCacheItemState
): UploadCardStatusType | undefined {
	switch(state.kind) {
		case "success":
			return "success";
		case "failed":
			return "failed";
		case "pending":
		case "uploading":
			return undefined;
		default:
			never(state);
	}
}